var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { addDays, differenceInDays, format, subDays } from "date-fns";
import { colors } from "healthex-common/colors";
import { HealthSystemStatsDto } from "healthex-common/stats.dtos";
import { pluralize } from "healthex-common/utils";
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { HealthSystemBaseViewModel } from "./HealthSystemBaseViewModel";
import { HXDesktopGraphLegendCirclePosition, } from "./HXDesktopStatViews";
export class HealthcareOrganizationAnalyticsViewModel extends HealthSystemBaseViewModel {
    constructor(container) {
        super(container);
        this.inviteUsersDialogOpen = false;
        this.statsManager = container.statsManager;
    }
    inviteUsers() {
        this.inviteUsersDialogOpen = true;
    }
    async inviteUsersDone() {
        this.inviteUsersDialogOpen = false;
    }
    get overviewTimelineDailyStatsForOrganization() {
        if (this.statsManager.overviewTimelineDailyStatsForOrganization.value === NOT_LOADED)
            return NOT_LOADED;
        if (this.statsManager.overviewTimelineDailyStatsForOrganization.value === null)
            return null;
        return this.statsManager.overviewTimelineDailyStatsForOrganization.value;
    }
    get overviewStatsForOrganization() {
        if (this.overviewTimelineDailyStatsForOrganization === NOT_LOADED)
            return NOT_LOADED;
        if (this.overviewTimelineDailyStatsForOrganization === null)
            return null;
        return this.overviewTimelineDailyStatsForOrganization[0];
    }
    get atAGlanceStatsInfo() {
        if (this.overviewStatsForOrganization === NOT_LOADED || this.overviewStatsForOrganization === null)
            return null;
        return [
            {
                total: this.overviewStatsForOrganization.totalAvailablePatients,
                text: "Patients available for\nresearch via HealthEx",
                tooltipText: "The number of patients within your organization who can be identified for participation in research studies facilitated through HealthEx.",
            },
            {
                total: this.overviewStatsForOrganization.totalPatientsCompletedOnboarding,
                text: "Patients who have\ncompleted onboarding",
                tooltipText: "The number of patients who have completed onboarding to the HealthEx platform.",
            },
            {
                total: this.overviewStatsForOrganization.totalPrescreenConsentedPatients +
                    this.overviewStatsForOrganization.totalEnrollmentConsentedPatients,
                text: "Patients participating in\nat least one study",
                tooltipText: "The number of patients who have consented to be prescreened or enrolled in a study.",
                adjustTooltipLeftPosition: "-23px",
            },
            {
                total: this.overviewStatsForOrganization.totalPatientsOptedOutOfHealthEx,
                text: "Patients who have opted\nout of using HealthEx",
                tooltipText: "The number of patients who have opted out of all contact and participation in research on the HealthEx platform.",
                adjustTooltipLeftPosition: "-12px",
            },
        ];
    }
    get patientEngagementBarGraphStatsInfo() {
        if (this.overviewStatsForOrganization === NOT_LOADED || this.overviewStatsForOrganization === null)
            return null;
        return {
            title: "Patient research participation breakdown",
            tooltipText: "Shows the number of patients who have reached various milestones on HealthEx. Each bar in the chart represents the number of patients who have reached a specific milestone.",
            columns: [
                {
                    text: "Completed\nonboarding",
                    total: this.overviewStatsForOrganization.totalPatientsCompletedOnboarding,
                    tooltipText: "The number of patients who have completed onboarding to the HealthEx platform.",
                },
                {
                    text: "Identified\nfor a study",
                    total: this.overviewStatsForOrganization.totalIdentifiedPatients,
                    tooltipText: "The number of patients within your organization who have been identified as meeting the initial eligibility criteria for a study facilitated through HealthEx.",
                },
                {
                    text: "Prescreen\nconsented for\nstudy eligibility",
                    total: this.overviewStatsForOrganization.totalPrescreenConsentedPatients,
                    tooltipText: "The number of patients who have provided consent for prescreening, enabling approved researchers to determine eligibility.",
                },
                {
                    text: "Enrolled\nin a study",
                    total: this.overviewStatsForOrganization.totalEnrollmentConsentedPatients,
                    tooltipText: "The number of patients who have provided consent to be enrolled in a study.",
                },
                {
                    text: "Identified data\naccessed by\na researcher",
                    total: this.overviewStatsForOrganization.totalIdentifiedRecordsAccessed,
                    tooltipText: "The number of patients whose identified data has been accessed by researchers collaborating on a study.",
                    adjustTooltipLeftPosition: "-15px",
                },
            ],
        };
    }
    get patientEngagementStatRatesInfo() {
        if (this.overviewStatsForOrganization === NOT_LOADED || this.overviewStatsForOrganization === null)
            return null;
        const { totalPrescreenConsentedPatients, totalRequestedPrescreenConsentedPatients, totalEnrollmentConsentedPatients, totalRequestedEnrollmentConsentedPatients, } = this.overviewStatsForOrganization;
        const totalPrescreenPatients = totalPrescreenConsentedPatients + totalRequestedPrescreenConsentedPatients;
        const totalEnrollmentPatients = totalEnrollmentConsentedPatients + totalRequestedEnrollmentConsentedPatients;
        return [
            {
                title: "Prescreen consent rate",
                statText: `${this.formatPercentage(totalPrescreenConsentedPatients / totalPrescreenPatients)}%`,
                text: `${totalPrescreenConsentedPatients}/${totalPrescreenPatients} patients who have been contacted for prescreening have consented`,
            },
            {
                title: "Enrollment consent rate",
                statText: `${this.formatPercentage(totalEnrollmentConsentedPatients / totalEnrollmentPatients)}%`,
                text: `${totalEnrollmentConsentedPatients}/${totalEnrollmentPatients} patients who have been contacted for enrollment have consented`,
            },
        ];
    }
    get patientResearchOverTimeStatDaysText() {
        if (this.overTimeStats === null)
            return null;
        return `Last ${this.overTimeStats.patientResearch.length} ${pluralize(this.overTimeStats.patientResearch.length, "day", "days")}`;
    }
    get healthSystemResearchOverTimeStatDaysText() {
        if (this.overTimeStats === null)
            return null;
        return `Last ${this.overTimeStats.patientResearch.length} ${pluralize(this.overTimeStats.patientResearch.length, "day", "days")}`;
    }
    getXAxisLabel(total, index, date) {
        const showLabel = (total > 0 && (index === 0 || index === total - 1)) ||
            index % 7 === 0;
        return showLabel ? format(new Date(date), "LLL dd") : "";
    }
    get overTimeStats() {
        if (this.overviewTimelineDailyStatsForOrganization === NOT_LOADED || this.overviewTimelineDailyStatsForOrganization === null) {
            return null;
        }
        let overTimeStats = this.fillInMissingStatDaysIfNecessary(this.overviewTimelineDailyStatsForOrganization, 90);
        overTimeStats = this.padStatDaysIfNecessary(overTimeStats, 2);
        overTimeStats = overTimeStats.reverse();
        const total = overTimeStats.length;
        return {
            patientResearch: overTimeStats.map((stats, index) => {
                const { date, totalPatientsCompletedOnboarding, totalIdentifiedPatients, totalPrescreenConsentedPatients, totalEnrollmentConsentedPatients, totalIdentifiedRecordsAccessed, } = stats;
                return [
                    this.getXAxisLabel(total, index, date),
                    totalPatientsCompletedOnboarding,
                    totalIdentifiedPatients,
                    totalPrescreenConsentedPatients,
                    totalEnrollmentConsentedPatients,
                    totalIdentifiedRecordsAccessed,
                ];
            }),
            healthSystemResearch: overTimeStats.map((stats, index) => {
                const { date, totalResearchStudies, totalPrescreenConsentedPatients, totalEnrollmentConsentedPatients } = stats;
                return [
                    this.getXAxisLabel(total, index, date),
                    totalResearchStudies,
                    totalPrescreenConsentedPatients + totalEnrollmentConsentedPatients,
                ];
            }),
        };
    }
    get patientResearchChartInfo() {
        if (this.overTimeStats === null)
            return null;
        return {
            chartData: [
                [
                    "Type",
                    "Completed onboarding",
                    "Identified for a study",
                    "Prescreen consented for a study eligibility",
                    "Enrolled in a study",
                    "Identified data accessed by a researcher",
                ],
                ...this.overTimeStats.patientResearch,
            ],
            legendInfo: {
                legends: [
                    { text: "Completed\nonboarding" },
                    { text: "Identified for a\nstudy" },
                    { text: "Prescreen\nconsented for\nstudy eligibility" },
                    { text: "Enrolled in a\nstudy" },
                    { text: "Identified data\naccessed by a\nresearcher" },
                ],
                marginLeft: "3%",
            },
        };
    }
    get healthSystemResearchInfo() {
        if (this.overTimeStats === null)
            return null;
        if (this.overviewStatsForOrganization === NOT_LOADED || this.overviewStatsForOrganization === null)
            return null;
        return {
            statsInfo: [
                {
                    title: "Research studies added by\nyour organization",
                    statText: this.overviewStatsForOrganization.totalResearchStudies,
                },
                {
                    title: "Conditions and diseases your\norganization is supporting",
                    statText: this.overviewStatsForOrganization.totalConditionsAndDiseases,
                    tooltipText: "The number of unique diseases, disorders, syndromes, illnesses, injuries, or other health related issues that are being studied.",
                    adjustTooltipLeftPosition: "-12px",
                },
            ],
            chartData: [
                [
                    { type: "string", label: "" },
                    { type: "number", label: "Number of studies approved by your organization via HealthEx" },
                    { type: "number", label: "Number of patients participating in at least one study" },
                ],
                ...this.overTimeStats.healthSystemResearch,
            ],
            legendInfo: {
                startLegend: {
                    text: "Number of studies added to HealthEx",
                    color: colors.accents_sunset,
                    circlePosition: HXDesktopGraphLegendCirclePosition.START,
                },
                endLegend: {
                    text: "Number of patients participating in at least one study",
                    color: colors.accents_sun,
                    circlePosition: HXDesktopGraphLegendCirclePosition.END,
                    alignEnd: true,
                },
                marginLeft: "3.0%",
                marginRight: "3.6%",
            },
        };
    }
    get sinceVisitedCurrentPageTimestamp() {
        if (this.appUIManager.sinceVisitedCurrentPageTimestamp === null)
            return null;
        return `As of ${this.appUIManager.sinceVisitedCurrentPageTimestamp.toLowerCase()}`;
    }
    fillInMissingStatDaysIfNecessary(stats, maxDays) {
        if (stats.length <= 1)
            return stats;
        const expectedLastStatDate = subDays(new Date(stats[0].date), stats.length - 1);
        const lastStatDate = new Date(stats[stats.length - 1].date);
        if (format(expectedLastStatDate, "yyyydd") === format(lastStatDate, "yyyydd")) {
            return stats;
        }
        const statsWithAddedMissingDays = [];
        for (const [statIndex, stat] of stats.entries()) {
            statsWithAddedMissingDays.push({ ...stat });
            if (statsWithAddedMissingDays.length >= maxDays)
                break;
            if (stats.length > statIndex + 1) {
                const currentStatDate = new Date(stat.date);
                const nextStatDate = new Date(stats[statIndex + 1].date);
                const differenceInDaysWithNextStat = differenceInDays(currentStatDate, nextStatDate);
                for (let filledInStatIndex = 1; filledInStatIndex < differenceInDaysWithNextStat; filledInStatIndex++) {
                    statsWithAddedMissingDays.push({ ...stat, date: subDays(currentStatDate, filledInStatIndex) });
                    if (statsWithAddedMissingDays.length >= maxDays)
                        break;
                }
            }
            if (statsWithAddedMissingDays.length >= maxDays)
                break;
        }
        return statsWithAddedMissingDays;
    }
    padStatDaysIfNecessary(stats, padDays) {
        var _a;
        const padTotal = padDays - stats.length;
        if (padTotal <= 0)
            return stats;
        const paddedStats = [...stats];
        const startDate = (_a = paddedStats[0]) === null || _a === void 0 ? void 0 : _a.date;
        let padDate = startDate ? new Date(startDate) : addDays(new Date(), 1);
        for (let i = 0; i < padTotal; i++) {
            padDate = subDays(padDate, 1);
            paddedStats.push(new HealthSystemStatsDto({
                date: padDate,
                totalAvailablePatients: 0,
                totalPatientsCompletedOnboarding: 0,
                totalPatientsOptedOutOfHealthEx: 0,
                totalEnrollmentConsentedPatients: 0,
                totalPrescreenConsentedPatients: 0,
                totalRequestedEnrollmentConsentedPatients: 0,
                totalRequestedPrescreenConsentedPatients: 0,
                totalIdentifiedPatients: 0,
                totalIdentifiedRecordsAccessed: 0,
                totalResearchStudies: 0,
                totalConditionsAndDiseases: 0,
            }));
        }
        return paddedStats;
    }
    goToStudyDetailsPage(studyId) {
        super.goToStudyDetailsPage(studyId);
    }
    formatPercentage(percentage) {
        if (!Number.isFinite(percentage)) {
            return "0";
        }
        return Math.min(100, Math.round(percentage * 100)).toFixed(0);
    }
    get isHome() {
        return this.appUIManager.homepage === "HEALTHCARE_ORG_ANALYTICS";
    }
}
__decorate([
    observable
], HealthcareOrganizationAnalyticsViewModel.prototype, "inviteUsersDialogOpen", void 0);
__decorate([
    computed
], HealthcareOrganizationAnalyticsViewModel.prototype, "overviewTimelineDailyStatsForOrganization", null);
__decorate([
    computed
], HealthcareOrganizationAnalyticsViewModel.prototype, "overviewStatsForOrganization", null);
__decorate([
    computed
], HealthcareOrganizationAnalyticsViewModel.prototype, "atAGlanceStatsInfo", null);
__decorate([
    computed
], HealthcareOrganizationAnalyticsViewModel.prototype, "patientEngagementBarGraphStatsInfo", null);
__decorate([
    computed
], HealthcareOrganizationAnalyticsViewModel.prototype, "patientEngagementStatRatesInfo", null);
__decorate([
    computed
], HealthcareOrganizationAnalyticsViewModel.prototype, "patientResearchOverTimeStatDaysText", null);
__decorate([
    computed
], HealthcareOrganizationAnalyticsViewModel.prototype, "healthSystemResearchOverTimeStatDaysText", null);
__decorate([
    computed
], HealthcareOrganizationAnalyticsViewModel.prototype, "overTimeStats", null);
__decorate([
    computed
], HealthcareOrganizationAnalyticsViewModel.prototype, "patientResearchChartInfo", null);
__decorate([
    computed
], HealthcareOrganizationAnalyticsViewModel.prototype, "healthSystemResearchInfo", null);
__decorate([
    computed
], HealthcareOrganizationAnalyticsViewModel.prototype, "sinceVisitedCurrentPageTimestamp", null);
__decorate([
    computed
], HealthcareOrganizationAnalyticsViewModel.prototype, "isHome", null);
